import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from "react-intl";
import { Typography, makeStyles, Button, Box, Checkbox, FormControlLabel } from '@material-ui/core';
import PropTypes from 'prop-types';
import { JSEncrypt } from 'jsencrypt';

import { FormTextField } from '../../../components';

import { ROUTES } from "../../../constants/routes";
import { getI18n } from "../../../utils/intl";
import { xssSanitize } from "../../../utils";
import { getAuthSchemePublicKey, getAuthenticationData } from '../../../reducers/login';
import { fetchAuthSchemePublicKey, authenticateLogin } from "../../../actions/login";
import { getConfig } from '../../../reducers/portalConfig';

import { loginFormReducer, initializeLoginFormState, ACTIONS } from '../reducer';
import { useLogin } from "../context";
import { LABELS, KEY_REFRESH_INTERVAL, UNKNOWN_ERROR, ERRORS, SchemeShape } from '../constants';
import {
  validateFieldValue, goToHomePage, goToResetPasswordPage,
  isEnhancedPasswordSecurityEnabled, isProtocolInWhitelist, isPublicKeyValid,
} from "../utils";
import WarningIcon from '../icons/warningIcon.svg';

import styles from './styles';

const isValidPublicKeyForScheme = (publicKey, keyUUID, schemeUUID) => {
  if (!keyUUID || !schemeUUID) return false;
  if (keyUUID !== schemeUUID) return false;

  return isPublicKeyValid(publicKey);
};

const LoginForm = ({ scheme, id }) => {
  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const globalDispatch = useDispatch();

  const {
      isEULArequired, provider, referrer, clearGlobalErrors,
      setHasDetectedMultipleSessions, hasDetectedMultipleSessions,
      setShouldShowCredentialsError, selectedAuthSchemeType,
  } = useLogin();
  const publicKeyData = useSelector(getAuthSchemePublicKey);
  const authenticationData = useSelector(getAuthenticationData);

  const { portal } = useSelector(getConfig);

  const [state, dispatch] = React.useReducer(
      loginFormReducer, null, initializeLoginFormState,
  );
  const [appLoadedTime, setAppLoadedTime] = React.useState(null);
  const [isLoginTriggered, setIsLoginTriggered] = React.useState(false);

  React.useEffect(() => {
    if (!appLoadedTime) {
      setAppLoadedTime(new Date());
    }
  }, []);

  React.useEffect(() => {
    globalDispatch(fetchAuthSchemePublicKey({ uuid: scheme.uuid }));
  }, [scheme.uuid]);

  React.useEffect(() => {
    if (publicKeyData.uuid !== scheme.uuid) return;
    if (publicKeyData.error) {
      //showing unknown error if unable to get public key
      errorRedirection(UNKNOWN_ERROR);
    }
    if (publicKeyData.data && isLoginTriggered) {
      authenticateUser();
    }
  }, [publicKeyData, isLoginTriggered]);

  React.useEffect(() => {
    if (authenticationData.data) {
      successRedirection(authenticationData.data);
      return;
    }
    if (authenticationData.error) {
      const { type, details } = authenticationData.error;
      if (type === ERRORS.PASSWORD_EXPIRY) {
        passwordExpiryRedirection(details);
        return;
      }
      if (type === ERRORS.MULTIPLE_SESSIONS) {
        handleUserMultipleSessions();
        return;
      }
      if (type === ERRORS.ERROR_CREDENTIALS) {
        setShouldShowCredentialsError(true);
        return;
      }
      if (type === ERRORS.ERROR_REDIRECT) {
       errorRedirection(details);
        return;
      }
      if (type === ERRORS.ERROR_REDIRECT_GENERIC) {
        window.location.href = ROUTES.genericErrorPage.path;
      }
    }
  }, [authenticationData]);

  const handleChange = (field, value) => {
    dispatch({ type: ACTIONS.UPDATE_FIELD_VALUE, payload: { field, value } });
    validateFieldValue({ field, dispatch, intl });
  }

  const onCancel = () => {
    goToHomePage();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isEnhancedPasswordSecurityEnabled(scheme)) {
      authenticateUser();
    } else {
      const currentTime = new Date();
      //check whether it is valid or has to refresh it
      const timeDiff = (currentTime.getTime() - appLoadedTime.getTime()) / 1000;
      if (timeDiff < KEY_REFRESH_INTERVAL) {
        authenticateUser();
      } else {
        globalDispatch(fetchAuthSchemePublicKey({ uuid: scheme.uuid }));
        setIsLoginTriggered(true);
      }
    }
  }

  const authenticateUser = () => {
    if (isEnhancedPasswordSecurityEnabled(scheme) &&
      !(isValidPublicKeyForScheme(publicKeyData.data?.publicKey, publicKeyData.uuid, scheme.uuid))
    ) {
      errorRedirection(UNKNOWN_ERROR);
      return;
    }
    const options = {
      hasDetectedMultipleSessions,
    }
    const payload = {
      password: state.password.value,
      username: state.userName.value,
      eula: isEULArequired ? state.isEULAaccepted.value ? 'accept' : 'reject' : 'accept',
    }
    if (selectedAuthSchemeType === 'external') {
      payload.provider = scheme.uuid;
    }
    if (referrer) {
      payload.referrer = xssSanitize(referrer);
    }
    if (hasDetectedMultipleSessions) {
      payload.terminateExistingSessions = true;
    }
    if (isEnhancedPasswordSecurityEnabled(scheme)) {
      const encrypt = new JSEncrypt();
      if (
        isValidPublicKeyForScheme(publicKeyData.data?.publicKey, publicKeyData.uuid, scheme.uuid)
      ) {
        encrypt.setPublicKey(publicKeyData.data.publicKey);
        payload.password = encrypt.encrypt(payload.password);
        options.publicKey = publicKeyData.data.publicKey;
      }
    }

    globalDispatch(authenticateLogin(payload, options));
  }

  const handleUserMultipleSessions = () => {
    setHasDetectedMultipleSessions(true);
    dispatch({ type: ACTIONS.UPDATE_FIELD_VALUE, payload: { field:'password', value: '' } });
    clearGlobalErrors();
  };

  const successRedirection = (response) => {
    window.localStorage.clear();
    if (response?.referrer) {
      const referrer = decodeURIComponent(response?.referrer);
      if (referrer.indexOf("//") >= 0) {
        errorRedirection(UNKNOWN_ERROR);
      } else if (isProtocolInWhitelist(referrer) === false) {
        errorRedirection(UNKNOWN_ERROR, true);
      } else {
        window.location.href = xssSanitize(referrer);
      }
    } else {
      const dashboardPath = response?.dashboardPath;
      if (dashboardPath) {
        const url = new URL(dashboardPath);
        window.location.href = url.pathname;
      } else {
        window.location.href = ROUTES.dashboardRedirect.path;
      }
    }
  };

  const errorRedirection = (errorCode, ignoreReferrer = false) => {
    let redirectionUrl = '/admin'
    if (provider) {
      redirectionUrl += '/login?provider=' + provider + '&';
    } else {
      redirectionUrl += '/login?';
    }
    if (referrer && !ignoreReferrer) {
      redirectionUrl += 'referrer=' + referrer + '&';
    }
    redirectionUrl += errorCode + '=true';
    window.location.href = redirectionUrl;
  };

  const passwordExpiryRedirection = (responseJSON) => {
    if (!responseJSON?.token) {
      window.location.href = ROUTES.genericErrorPage.path;
      return;
    }
    window.location.href = `${ROUTES.newPasswordRedirect.path}?token=${responseJSON.token}`;
  };

  const disableSubmitButton = publicKeyData.isLoading || authenticationData.isLoading;
  const shouldShowForgotPassword = !hasDetectedMultipleSessions && selectedAuthSchemeType === 'internal';
  const shouldShowEULACheckbox = isEULArequired && selectedAuthSchemeType === 'internal';

  return (
    <form onSubmit={(e) => handleSubmit(e)}
      className={classes.loginForm} id={id}
    >
      {hasDetectedMultipleSessions
        ? <>
            <Typography variant='h3' className={classes.multipleSessionHeader}>
              <img src={WarningIcon} />
              {intl.getI18nMessage(LABELS.multipleSessionsHeading)}
            </Typography>
            <Typography >
              {intl.getI18nMessage(LABELS.multipleSessionsText)}
            </Typography>
          </>
        : (
          <FormTextField
            id='form-userName'
            name={intl.getI18nMessage(LABELS.userName)}
            placeholder={intl.getI18nMessage(LABELS.userName)}
            hideLabel
            textFieldClass={classes.textField}
            type={'text'}
            fieldContainerClass={classes.fieldContainer}
            helperText={
              state.userName.errors.length > 0
                ? state.userName.errors[0]
                : null
            }
            errorHelperText={state.userName.errors.length > 0}
            error={state.userName.errors.length > 0}
            value={state.userName.value}
            handleChange={value => handleChange('userName', value)}
            InputProps={{
              name: 'username',
            }}
          />
        )
      }

      <FormTextField
        id='form-password'
        type='password'
        hideLabel={!hasDetectedMultipleSessions}
        name={intl.getI18nMessage(LABELS.password)}
        placeholder={intl.getI18nMessage(LABELS.password)}
        textFieldClass={classes.textField}
        fieldContainerClass={classes.fieldContainer}
        helperText={state.password.errors.length > 0 ? state.password.errors[0] : null}
        errorHelperText={state.password.errors.length > 0}
        error={state.password.errors.length > 0}
        value={state.password.value}
        handleChange={value => handleChange('password', value)}
      />

      {shouldShowEULACheckbox
        ? <>
          <FormControlLabel
            control={
              <Checkbox
                id="eula-agreement-checkbox"
                onChange={(event) => handleChange('isEULAaccepted', event.target.checked)}
                checked={state.isEULAaccepted.value}
              />
            }
            label={
              <>
                {intl.getI18nMessage(LABELS.eulaAgreementMessage)}
                <a href={`/api/${portal.tenantPrefix}/app/eula`} target="eula" id="eula-text">
                  {intl.getI18nMessage(LABELS.licenseAgreementText)}
                </a>
              </>
            }
            classes={{ label: classes.eulaCheckboxLabel }}
          />
        </> : null
      }

      <Box className={classes.actionButtons}>
        <Button
          data-apim-test="login-submit" variant="contained"
          color="secondary" type='submit'
          disabled={disableSubmitButton}
          classes={{
            root: classes.buttonStyles, startIcon: classes.buttonIcon,
          }}
        >
          <Typography className={classes.buttonContent} color="inherit" >
            {intl.getI18nMessage(
              hasDetectedMultipleSessions ? LABELS.multipleSessionLoginButton :LABELS.loginButton,
            )}
          </Typography>
        </Button>
        <Button
          data-apim-test="login-cancel"
          variant="outlined" color='secondary'
          classes={{
            root: classes.buttonStyles, startIcon: classes.buttonIcon,
          }}
          onClick={onCancel}
        >
          <Typography className={classes.buttonContent} color='inherit'>
            {intl.getI18nMessage(LABELS.cancelButton)}
          </Typography>
        </Button>
      </Box>

      {shouldShowForgotPassword
        ? (
          <Button
            onClick={goToResetPasswordPage}
            variant='text' color='secondary' data-apim-test="forgot-password-text"
            classes={{ root: classes.forgotPasswordButton }}
          >
            <Typography className={classes.buttonContent} color='inherit'>
              {intl.getI18nMessage(LABELS.forgotPassword)}
            </Typography>
          </Button>
        ) : null
      }
    </form>
  )
}

LoginForm.propTypes = {
  id: PropTypes.string,
  scheme: PropTypes.shape(SchemeShape),
}

export default LoginForm;